import React, { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { PrevLocationProps, ResourceLocationWithPrev } from '@/api/product/types';
import { Button } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { BreadCrumbModel } from '@/generated/data.schemas';
import { useGetTranslations } from '@/generated/translations';
import { Icon } from '../Icon';
import { Link } from '../Link';
import styles from './Breadcrumbs.module.scss';

interface BreadcrumbsProps {
	className?: string;
	/**
	 * The container the crumbs are held in. Set on the inner wrapper rather than through
	 * `className`, which lands on the band outside it: that one stays full width so the
	 * inverse background reaches the edges of the viewport.
	 */
	containerClassName?: string;
	breadCrumbs: BreadCrumbModel[] | null;
	hasNoMobileSpacer?: boolean;
	variant?: 'default' | 'inverse';
	hasBack?: boolean;
}

interface StructuredDataItemProps {
	'@type': 'ListItem';
	position: number;
	item: {
		'@id': string;
		name: string;
	};
}

/**
 * Generic Breadcrumbs component, used for showing path for current page.
 * Shown on CMS, PLP & PDP pages.
 */
export const Breadcrumbs: React.FunctionComponent<BreadcrumbsProps> = ({
	className,
	containerClassName = 'u-container-lg',
	breadCrumbs,
	hasNoMobileSpacer,
	variant,
	hasBack,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);

	const navigate = useNavigate();
	const location = useLocation();

	if (!breadCrumbs || breadCrumbs.length < 2) {
		return null;
	}

	const handleNavigation = () => {
		// if prev location is saved in state on product card go back to make a CSR
		const routing = location.state as undefined | ResourceLocationWithPrev;

		if (hasBack && routing && routing?.prevLocation != null) {
			const prevLocation: PrevLocationProps = routing.prevLocation;

			navigate(
				{
					pathname: prevLocation.pathname,
					search: prevLocation.search,
				},
				{
					state: { ...prevLocation.state },
				},
			);
		} else {
			navigate(-1);
		}
	};

	const setStructuredItems = (): StructuredDataItemProps[] => {
		const arr: StructuredDataItemProps[] = [];

		breadCrumbs.forEach((item, index) => {
			arr.push({
				'@type': 'ListItem',
				position: index + 1,
				item: {
					'@id': item.link || '',
					name: item.text || '',
				},
			});
		});

		return arr;
	};

	const structuredData = {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: setStructuredItems(),
	};

	return (
		<nav
			className={classNames(
				styles.wrapper,
				'u-top-content-spacer',
				{ ['u-top-content-spacer-no-mobile']: hasNoMobileSpacer },
				{ [styles.isInverse]: variant === 'inverse' },
				className,
			)}
			aria-label={translations?.data.shared.navigation.breadcrumbs}
		>
			<div className={classNames(styles.innerWrapper, containerClassName)}>
				{hasBack && (
					<>
						<Button
							variant={'ghost'}
							onClick={handleNavigation}
							aria-label={translations?.data.shared.back}
						>
							<Icon name={'arrowLeft'} size={'md'} />

							<span className={styles.backBtnName}>{translations?.data.shared.back}</span>
						</Button>
						<span className={styles.separator} />
					</>
				)}

				<ol className={styles.list}>
					{breadCrumbs.map((item, index) => {
						const notLastItem = breadCrumbs.length !== index + 1;

						if (notLastItem) {
							return (
								<li className={styles.item} key={`${item.text}${index}`}>
									<Link
										className={classNames(styles.link, {
											[styles.hasNoUrl]: !item.link && !item.to && !item.route,
										})}
										linkUrl={item.link}
										to={item.to}
										route={item.route}
										color={'active'}
										textLink
										hasMinHeight
									>
										{item.text}
									</Link>
									<Icon className={styles.icon} name={'arrowLeft'} size={'md'} rotate={'180'} />
								</li>
							);
						}

						return (
							<li className={styles.item} key={`${item.text}${index}`}>
								<Link
									hasMinHeight
									className={classNames(styles.link, styles.hasNoUrl)}
									aria-current={'page'}
								>
									{item.text}
								</Link>
							</li>
						);
					})}
				</ol>
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
				/>
			</div>
		</nav>
	);
};

export const MemoizedBreadcrumbs = React.memo(Breadcrumbs);
